(function() {
  'use strict';

  angular.module('ringbaPortal.services').factory('billingService', billingService);

  function billingService(
    $http,
    ngAuthSettings,
    Upload,
    userSettingsService,
    promiseCacheService
  ) {
    const serviceBaseUri = ngAuthSettings.apiServiceBaseUri,
      uploadKycNotificationSettings = {
        hideUploadKycNotification: true
      };

    const service = {
      getBilling: getBilling,
      getCreditCards: getCreditCards,
      getBalance:  getBalance,
      getBillingSettings: getBillingSettings,
      updateBillingSettings: updateBillingSettings,
      getCCTransactions: getCCTransactions,
      addCreditCard: addCreditCard,
      verifyCard: verifyCard,
      deleteCreditCard: deleteCreditCard,
      updateCreditCard: updateCreditCard,
      addAccountBalance: addAccountBalance,
      changePlanType: changePlanType,
      getBillingPlans: getBillingPlans,
      getPricingPlan: getPricingPlan,
      checkUploadKycNotificationSettings: checkUploadKycNotificationSettings,
      uploadKycNotificationSettings: uploadKycNotificationSettings,
      setUploadKycNotificationSettings: setUploadKycNotificationSettings,
      getTCPABillingInfo: getTCPABillingInfo,
      getTCPABillingPlans: getTCPABillingPlans
    };

    return service;

    function getBilling (accountId) {
      const url = serviceBaseUri + accountId + '/billing';
      return $http.get(url);
    }

    function getCreditCards (accountId) {
      const url = serviceBaseUri + accountId + '/billing/CreditCards';
      return $http.get(url);
    }

    function getBalance (accountId) {
      const url = serviceBaseUri + accountId + '/billing/Balance';
      return $http.get(url);
    }

    function getBillingSettings (accountId) {
      const url = serviceBaseUri + accountId + '/billing/BillingSettings';
      return $http.get(url);
    }

    function getPricingPlan (accountId) {
      const url = serviceBaseUri + accountId + '/billing/pricing';
      return $http.get(url);
    }

    function updateBillingSettings (accountId, billingSettings) {
      const url = serviceBaseUri + accountId + '/billing/BillingSettings';
      return $http.put(url, billingSettings);
    }

    function getCCTransactions (accountId, pastDays, days, pastMonths, months) {
      let params;
      if (pastMonths && months) {
        params = { pastMonths: pastMonths, months: months };
      } else if (pastDays && days) {
        params = { pastDays: pastDays, days: days };
      } else {
        params = { pastMonths: 36, months: 37};
      }
      const url = serviceBaseUri + accountId + '/billing/CCTransactions';
      return $http({
        method: 'GET',
        url: url,
        params: params
      });
    }

    function addCreditCard (accountId, data) {
      const url = serviceBaseUri + accountId + '/billing/CreditCards';
      return $http.post(url, data);
    }

    function verifyCard (accountId, kycFiles, cardDetails) {
      const url = serviceBaseUri + accountId + '/billing/CreditCards/' + cardDetails.id + '/verify';

      return Upload.upload({
        url: url,
        method: 'PUT',
        data: {
          file: kycFiles
        }
      }).then(function (response) {
        // Hide notification to upload KYC details
        setUploadKycNotificationSettings(true);
        return response;
      });
    }

    function deleteCreditCard (accountId, creditCardId) {
      const url = serviceBaseUri + accountId + '/billing/CreditCards/' + creditCardId;
      return $http.delete(url).then(function (response) {
        // _checkCardVerified function checks whether we need to show the upload KYC details again
        _checkCardVerified(response.data.creditCards);
        return response;
      });
    }

    function updateCreditCard (accountId, creditCardId, data) {
      const url = serviceBaseUri + accountId + '/billing/CreditCards/' + creditCardId;
      return $http.put(url, data);
    }

    function addAccountBalance (accountId, data) {
      const url = serviceBaseUri + accountId + '/billing/Balance';
      return $http.post(url, data);
    }

    function getBillingPlans(accountId) {
      const url = serviceBaseUri + accountId + '/billing/plans';
      return $http.get(url).then(function (response) {
        for (let i = 0; i < response.data.plans.length; i++) {
          const plan = response.data.plans[i];
          plan.displayName = getPlanDisplayName(plan.id);
        }
        return response.data.plans;
      });
    }

    function changePlanType (accountId, planId) {
      const url = serviceBaseUri + accountId + '/billing/plans';
      const data = { planId: planId };
      return $http.put(url, data);
    }

    function checkUploadKycNotificationSettings(accountId) {
      return userSettingsService.getUserSettings().then(function (userSettings) {
        if (userSettings.creditCardVerification && typeof userSettings.creditCardVerification.hideUploadKycNotification === 'boolean') {
          uploadKycNotificationSettings.hideUploadKycNotification = userSettings.creditCardVerification.hideUploadKycNotification;
        }
        return getCreditCards(accountId).then(function(cc){
          if(cc.data && cc.data.creditCards){
            _checkCardVerified(cc.data.creditCards);
          }
        });
      });
    }

    function setUploadKycNotificationSettings(hide) {
      return userSettingsService.getUserSettings().then(function (userSettings) {
        userSettings.creditCardVerification = userSettings.creditCardVerification || {};
        userSettings.creditCardVerification.hideUploadKycNotification = hide;
        uploadKycNotificationSettings.hideUploadKycNotification = hide;
        return userSettingsService.saveUserSettings(userSettings);
      });
    }

    function getTCPABillingInfo(accountId) {
      const url = serviceBaseUri + accountId + '/TCPAShield';
      return $http.get(url).then(function (response) {
        return response.data;
      });
    }

    function getTCPABillingPlans(accountId) {
      const url = serviceBaseUri + accountId + '/TCPAShield/plans';
      const cacheKey = 'tcpa-shield-pricing';

      return promiseCacheService.getCallWithCachedPromise(cacheKey, url, 800,function (response) {
        return response.data;
      });
    }

    function getPlanDisplayName(planId) {
      if (planId === 'SMALL BUSINESS') {
        return 'Basic';
      }

      if (planId === 'BASIC') {
        return 'Premium';
      }

      if (planId === 'CUSTOM' || planId === 'Custom' || planId === 'ENTERPRISECustom') {
        return 'Enterprise';
      }

      if (planId.endsWith('Custom')) {
        return planId.replace('Custom', ' - Custom');
      }

      return planId;
    }

    function _checkCardVerified(cards) {
      if(cards.length > 0){
        const verifiedCardExists = _.some(cards, function (card) {
          return card.status === 'Approved' || card.status === 'ApprovalPending';
        });
        setUploadKycNotificationSettings(verifiedCardExists);
      } else {
        //no cards so dont show warning
        setUploadKycNotificationSettings(true);
      }
    }

  }
})();
